/**
 * The inspector panel (W2-06; DESIGN.md §4.5 "Inspector (320 px)", §4.11; slot `chart.inspector`).
 *
 * Renders every registered inspector section (this package's and other packages') that applies to the selection,
 * in order: one section per selected kind, the N LINES section for several lines, the active line when nothing
 * else is selected. The header holds the Full-level units toggle (friendly / RPE raw), the Raw toggle (shows the
 * Raw dock tab with the exact JSON) and `{settings-2}` Project settings. Each section has its own error boundary,
 * so a failing section never takes the panel down. The panel answers `inspector.focusField` requests.
 */

import { Component, useEffect, useRef, type ReactNode } from 'react';
import { DIALOG } from '../../app/dialogs/types';
import type { AppServices, InspectorSectionDef, PanelProps } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import type { Selection } from '../../state/types';
import { EmptyState, IconButton } from '../../ui';
import { focusFieldIn, onFocusFieldRequest } from './focusField';
import { useEditorValue, useServicesStore, useSettings } from './hooks';
import { m } from './inspector.messages';
import { Callout } from './parts';
import s from './Inspector.module.css';

/** Sections that apply to `selection`, in order. */
export function applicableSections(services: AppServices, selection: Selection): readonly InspectorSectionDef[] {
  if (services.store === null) return [];
  return [...services.registry.inspectorSections()]
    .filter((def) => {
      try {
        return def.appliesTo(selection, services);
      } catch {
        return false;
      }
    })
    .sort((a, b) => a.order - b.order);
}

/** The Raw dock tab, when a package registers one. */
function rawPanelId(services: AppServices): string | null {
  return services.registry.panels('chart.dock').find((p) => p.id.toLowerCase().includes('raw'))?.id ?? null;
}

interface BoundaryProps {
  readonly children: ReactNode;
  readonly fallback: ReactNode;
  readonly resetKey: string;
}

/** Keeps one failing section from taking the panel down; retries when the selection changes. */
class SectionBoundary extends Component<BoundaryProps, { readonly failed: boolean; readonly key: string }> {
  override state = { failed: false, key: this.props.resetKey };
  static getDerivedStateFromError(): { readonly failed: boolean } {
    return { failed: true };
  }
  static getDerivedStateFromProps(props: BoundaryProps, state: { readonly failed: boolean; readonly key: string }) {
    return props.resetKey === state.key ? null : { failed: false, key: props.resetKey };
  }
  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function selectionKey(sel: Selection): string {
  const part = (set: ReadonlySet<number>): string => [...set].join(',');
  return [part(sel.notes), part(sel.keys), part(sel.segments), part(sel.lines), part(sel.bpm), part(sel.markers)].join(
    '|',
  );
}

export function InspectorPanel(props: PanelProps) {
  const { services } = props;
  const t = useT();
  const store = useServicesStore(services);
  const selection = useEditorValue(services, (st) => st.selection);
  // The line section follows the active line when nothing else is selected.
  const activeLine = useEditorValue(services, (st) => st.activeLine);
  const settings = useSettings(services);
  const level = settings.general.level;
  const rawUnits = settings.editing.units === 'rpe';
  const root = useRef<HTMLDivElement>(null);

  useEffect(
    () =>
      onFocusFieldRequest((field) => {
        const el = root.current;
        return el === null ? false : focusFieldIn(el, field);
      }),
    [],
  );

  const sections = store === null ? [] : applicableSections(services, selection);
  const raw = rawPanelId(services);
  const resetKey = `${selectionKey(selection)}#${String(activeLine)}`;

  return (
    <div ref={root} className={s.panel} data-region="inspector" aria-label={t(m.panelLabel)} role="region">
      <header className={s.header}>
        <span className={s.headerTitle}>{t(m.panelTitle)}</span>
        <div className={s.headerActions}>
          {level === 'full' && (
            <IconButton
              icon="Ruler"
              size="sm"
              label={{ key: rawUnits ? m.unitsRaw : m.unitsFriendly }}
              pressed={rawUnits}
              command="inspector.toggleUnits"
              onClick={() => {
                services.settings.set('editing', 'units', rawUnits ? 'friendly' : 'rpe');
              }}
            />
          )}
          {level === 'full' && (
            <IconButton
              icon="Braces"
              size="sm"
              label={{ key: m.rawToggle }}
              disabledReason={raw === null ? { key: m.rawUnavailable } : null}
              onClick={() => {
                if (raw !== null) services.ui.showPanel(raw);
              }}
            />
          )}
          <IconButton
            icon="Settings2"
            size="sm"
            label={{ key: m.projectSettings }}
            command="project.settings"
            disabledReason={store === null ? { key: m.needProject } : null}
            onClick={() => {
              void services.ui.openDialog(DIALOG.projectSettings, {});
            }}
          />
        </div>
      </header>
      <div className={s.body}>
        {store === null ? (
          <div className={s.empty}>
            <EmptyState icon="FolderOpen" title={{ key: m.noProject }} />
          </div>
        ) : sections.length === 0 ? (
          <div className={s.empty}>
            <EmptyState icon="MousePointerClick" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />
          </div>
        ) : (
          sections.map((def) => {
            const Section = def.component;
            return (
              <div key={def.id} data-section={def.id}>
                <SectionBoundary resetKey={resetKey} fallback={<Callout tone="danger">{t(m.sectionFailed)}</Callout>}>
                  <Section services={services} selection={selection} />
                </SectionBoundary>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
